import { useEffect, useState, useCallback } from "react";
import AdminHeader from "../components/admin/AdminHeader";
import { Link } from "react-router-dom";
import {
  ArrowLeft, ShieldCheck, Loader2, ScanLine,
  CheckCircle2, XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  fetchAdminBarcodeSubmissions,
  approveBarcodeSubmission,
  rejectBarcodeSubmission,
} from "../lib/api";
import { formatApiError } from "../lib/apiError";

const STATUS_TONE = {
  PENDING:  "bg-amber-50 border-amber-200 text-amber-800",
  APPROVED: "bg-emerald-50 border-emerald-200 text-emerald-800",
  REJECTED: "bg-red-50 border-red-200 text-red-800",
};

function StatTile({ label, value, tone }) {
  const TONES = {
    amber:  "bg-amber-50 border-amber-200 text-amber-900",
    green:  "bg-emerald-50 border-emerald-200 text-emerald-900",
    red:    "bg-red-50 border-red-200 text-red-900",
  };
  return (
    <div
      className={`rounded-xl border p-3 text-center ${TONES[tone]}`}
      data-testid={`barcode-summary-${label.toLowerCase()}`}
    >
      <div className="text-[10px] uppercase tracking-wider opacity-70 font-bold">{label}</div>
      <div className="text-2xl font-bold tabular-nums mt-0.5">{value ?? 0}</div>
    </div>
  );
}

function BarcodeSubmissionCard({ sub, onAction }) {
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState("");

  const act = async (kind) => {
    setBusy(true);
    try {
      const fn = kind === "approve" ? approveBarcodeSubmission : rejectBarcodeSubmission;
      await fn(sub.id, { notes: notes || undefined });
      toast.success(kind === "approve" ? "Approved & barcode applied." : "Rejected");
      onAction(sub.id, kind === "approve" ? "APPROVED" : "REJECTED");
    } catch (e) {
      toast.error(formatApiError(e, `${kind} failed`));
    } finally {
      setBusy(false);
    }
  };

  return (
    <article
      data-testid={`barcode-card-${sub.id}`}
      className="rounded-2xl border border-stone-200 bg-white shadow-sm overflow-hidden"
    >
      <div className="flex items-center gap-3 px-5 py-3.5 border-b border-stone-100 bg-stone-50/70 flex-wrap">
        <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${STATUS_TONE[sub.status] || ""}`}>
          {sub.status}
        </span>
        <span className="ml-auto text-[11px] text-stone-400 font-mono">
          {new Date(sub.submitted_at).toLocaleDateString()}
        </span>
      </div>

      <div className="px-5 py-4 space-y-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-stone-500 mb-0.5">
            Product
          </div>
          <Link
            to={`/products/${sub.product_slug || sub.product_id}`}
            target="_blank"
            rel="noreferrer"
            className="text-sm font-semibold text-stone-900 hover:underline"
          >
            {sub.product_brand ? `${sub.product_brand} · ` : ""}{sub.product_name}
          </Link>
        </div>
        <div className="flex items-baseline gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-stone-500 mb-0.5">
              Submitted barcode
            </div>
            <p
              className="text-base font-mono tracking-wider tabular-nums text-stone-900 bg-stone-50 border border-stone-200 rounded-xl px-3 py-2 inline-block"
              data-testid={`barcode-value-${sub.id}`}
            >
              {sub.barcode}
            </p>
          </div>
          <a
            href={`https://world.openbeautyfacts.org/product/${encodeURIComponent(sub.barcode)}`}
            target="_blank"
            rel="noreferrer"
            className="text-[12px] underline decoration-dotted text-stone-600 hover:text-stone-900"
          >
            Verify on Open Beauty Facts ↗
          </a>
        </div>
        {sub.note && (
          <div className="text-xs text-stone-600">
            <span className="text-[10px] uppercase tracking-wider font-bold text-stone-500">Reporter note: </span>
            {sub.note}
          </div>
        )}
        {sub.reporter_email && (
          <div className="text-[11px] text-stone-500 font-mono">{sub.reporter_email}</div>
        )}
        {sub.resolution_notes && (
          <div className="rounded-xl border border-stone-200 bg-white p-3">
            <div className="text-[10px] uppercase tracking-wider font-bold text-stone-500 mb-1">
              Reviewer notes
            </div>
            <p className="text-xs text-stone-700">{sub.resolution_notes}</p>
            {sub.reviewed_by && (
              <p className="text-[10px] text-stone-400 mt-1">— {sub.reviewed_by}</p>
            )}
          </div>
        )}
      </div>

      {sub.status === "PENDING" && (
        <>
          <div className="border-t border-stone-100 bg-stone-50 px-5 py-3">
            <label className="block">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">
                Reviewer notes (optional)
              </span>
              <input
                data-testid={`barcode-notes-${sub.id}`}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Verified against OBF listing."
                className="mt-1 w-full h-9 px-2 rounded border border-stone-300 bg-white text-sm"
              />
            </label>
          </div>
          <div className="flex items-center gap-2 px-5 py-3 border-t border-stone-100 flex-wrap">
            <Button
              onClick={() => act("approve")}
              disabled={busy}
              data-testid={`barcode-approve-${sub.id}`}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
              Approve & apply
            </Button>
            <Button
              variant="ghost"
              onClick={() => act("reject")}
              disabled={busy}
              data-testid={`barcode-reject-${sub.id}`}
              className="text-red-600 hover:bg-red-50"
            >
              <XCircle className="h-4 w-4 mr-1.5" /> Reject
            </Button>
          </div>
        </>
      )}
    </article>
  );
}

export default function AdminBarcodeSubmissions() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("PENDING");

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchAdminBarcodeSubmissions({
        limit: 200,
        status: statusFilter || undefined,
      });
      setData(res);
    } catch (e) {
      toast.error(formatApiError(e, "Failed to load submissions"));
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { reload(); }, [reload]);

  const onAction = (id, status) => {
    setData((prev) => prev ? {
      ...prev,
      items: prev.items.map((s) => s.id === id ? { ...s, status } : s),
    } : prev);
    setTimeout(reload, 200);
  };

  const summary = data?.summary || { pending: 0, approved: 0, rejected: 0 };

  return (
    <main
      data-testid="admin-barcode-submissions-page"
      className="min-h-screen bg-[#fdf8f3] px-4 sm:px-6 py-10 sm:py-12"
    >
      <div className="mx-auto max-w-5xl">
        <AdminHeader />

        <header className="mt-5 mb-6 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-stone-900 flex items-center gap-2">
              <ScanLine className="h-7 w-7 text-stone-700" />
              Barcode submissions
            </h1>
            <p className="mt-1.5 text-sm text-stone-600 max-w-xl">
              User-submitted UPC/EAN codes for products without a barcode on file.
              Approving writes the code onto the product so it becomes scannable.
            </p>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 bg-white px-2.5 py-1 text-[10px] uppercase tracking-wider text-stone-600">
            <ShieldCheck className="h-3 w-3" /> Admin
          </span>
        </header>

        <div className="grid grid-cols-3 gap-2 mb-5">
          <StatTile label="Pending"  value={summary.pending}  tone="amber" />
          <StatTile label="Approved" value={summary.approved} tone="green" />
          <StatTile label="Rejected" value={summary.rejected} tone="red" />
        </div>

        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <span className="text-xs text-stone-500">Filter by status:</span>
          {["", "PENDING", "APPROVED", "REJECTED"].map((s) => (
            <button
              key={s || "all"}
              onClick={() => setStatusFilter(s)}
              data-testid={`barcode-submissions-filter-${(s || "all").toLowerCase()}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                statusFilter === s
                  ? "bg-stone-900 text-white border-stone-900"
                  : "bg-white text-stone-700 border-stone-300 hover:border-stone-400"
              }`}
            >
              {s || "All"}
            </button>
          ))}
          <button
            onClick={reload}
            data-testid="barcode-submissions-refresh"
            className="ml-auto text-xs text-stone-500 hover:text-stone-900 px-2 py-1.5"
          >
            Refresh
          </button>
        </div>

        {loading ? (
          <div className="rounded-2xl border border-stone-200 bg-white p-10 text-center text-stone-400">
            <Loader2 className="h-5 w-5 animate-spin mx-auto" />
          </div>
        ) : (data?.items?.length ?? 0) === 0 ? (
          <div
            data-testid="barcode-submissions-empty-state"
            className="rounded-2xl border border-dashed border-stone-200 bg-white p-12 text-center"
          >
            <ScanLine className="h-10 w-10 text-stone-400 mx-auto" />
            <p className="mt-3 text-sm font-semibold text-stone-700">Nothing in this bucket.</p>
            <p className="mt-1 text-xs text-stone-500">User-submitted barcodes show up here for review.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="barcode-submissions-list">
            {data.items.map((sub) => (
              <BarcodeSubmissionCard key={sub.id} sub={sub} onAction={onAction} />
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
